import { useCallback, useState } from 'react';

import { toastify, useTranslation } from '@testsigma/atoms';

import { useSFConnectionState } from '@contexts/sf-connection-context';

import { SalesforceConnectionsService } from '@modules/salesforce/services';

export const useDeleteConnection = () => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const {
    selectedConnection,
    handleHideDeleteModal,
    handleUpdateOverlayMode,
    handleReload,
    handleUpdateSelectedConnection,
  } = useSFConnectionState();

  const handleSuccess = useCallback(() => {
    setIsDeleting(false);

    toastify(
      t('common.deleted.success', { name: t('user.connection.label') }),
      {
        type: 'success',
        position: 'top-center',
        hideProgressBar: true,
      },
    );
    handleHideDeleteModal();
    handleUpdateOverlayMode('none');
    handleUpdateSelectedConnection(null);
    handleReload();
  }, [
    t,
    handleHideDeleteModal,
    handleReload,
    handleUpdateOverlayMode,
    handleUpdateSelectedConnection,
  ]);

  const handleFailure = useCallback(
    (response: any) => {
      setIsDeleting(false);

      const errorMessage = response?.error?.code
        ? t(response.error.code)
        : t('common.deleted.failure', { name: t('user.connection.label') });

      toastify(errorMessage, {
        type: 'error',
        position: 'top-center',
        hideProgressBar: true,
      });
    },
    [t],
  );

  const handleDelete = useCallback(() => {
    if (!selectedConnection?.id) {
      return;
    }
    setIsDeleting(true);
    SalesforceConnectionsService.remove(selectedConnection?.id)
      .then(handleSuccess)
      .catch(handleFailure);
  }, [handleFailure, handleSuccess, selectedConnection?.id]);

  const handleCancel = () => {
    handleHideDeleteModal();
  };

  return {
    isDeleting,
    handleDelete,
    handleCancel,
  };
};
